/**
 * Argon2id password / recovery-code KDF via libsodium `crypto_pwhash`.
 *
 * Produces a 32-byte KEK from a passphrase (or recovery-code bytes) + a 16-byte
 * per-user salt. The memory parameter is expressed in MiB at the API boundary
 * and converted to bytes for libsodium's `memlimit`.
 */
import { getSodium } from './sodium.js';
import { wipe } from './memory.js';
import { asKek } from './types.js';
import { Argon2idParamError } from './errors.js';
/** Production params — m=512 MiB, t=3, p=1. Spec §2. */
export const ARGON2ID_PARAMS_V1 = {
    m: 512,
    t: 3,
    p: 1,
    outputLen: 32,
};
/**
 * Fallback params — m=256 MiB, t=3, p=1. Prewired (R1) so the 512→256 switch is
 * config, not code. Ships dormant; requires written external-reviewer sign-off
 * before it may be selected in production. Spec §2.
 */
export const ARGON2ID_PARAMS_FALLBACK_256 = {
    m: 256,
    t: 3,
    p: 1,
    outputLen: 32,
};
/**
 * Test-only params — m=8 MiB so property suites can run thousands of derivations
 * cheaply. NOT re-exported from the public barrel (`index.ts`); reachable only
 * via the `@heivol-locker/crypto/internal` subpath, which CI forbids importing
 * from `apps/*`. Spec/architecture §7.
 *
 * These params are NOT in the production allow-list. `deriveKek` rejects them
 * unless a test harness has explicitly called `__setAllowTestParams(true)` (also
 * internal-only). This closes the latent KDF-downgrade vector: once Phase 1
 * stores/returns server-supplied `umk_envelopes.params`, a tampered m=8 row
 * cannot be accepted on the wire path because production never flips the flag.
 */
export const ARGON2ID_PARAMS_TEST = {
    m: 8,
    t: 1,
    p: 1,
    outputLen: 32,
};
const MIB = 1024 * 1024;
/**
 * Opt-in for the cheap m=8 test params. Defaults `false` (production). Only the
 * `./internal` subpath exposes the setter, and CI forbids `apps/*` from importing
 * it — so the wire path can never accept ARGON2ID_PARAMS_TEST in production.
 */
let allowTestParams = false;
/** TEST-ONLY (via `./internal`). Enable/disable acceptance of ARGON2ID_PARAMS_TEST. */
export function __setAllowTestParams(value) {
    allowTestParams = value;
}
function sameCost(a, b) {
    return a.m === b.m && a.t === b.t && a.p === b.p;
}
function isAllowedParams(p) {
    return (sameCost(p, ARGON2ID_PARAMS_V1) ||
        sameCost(p, ARGON2ID_PARAMS_FALLBACK_256) ||
        (allowTestParams && sameCost(p, ARGON2ID_PARAMS_TEST)));
}
/**
 * Derive a 32-byte KEK. libsodium only supports `p=1`, matching both allowed
 * param sets. The caller-owned `passphrase` carrier is wiped before return.
 *
 * Note: an attacker-controlled `params` is rejected (E_ARGON_PARAMS) to prevent
 * a silent m=8 downgrade attack via a tampered envelope `params` column.
 */
export async function deriveKek(args) {
    const params = args.params ?? ARGON2ID_PARAMS_V1;
    if (!isAllowedParams(params)) {
        throw new Argon2idParamError();
    }
    const s = getSodium();
    if (args.salt.length !== s.crypto_pwhash_SALTBYTES) {
        throw new Argon2idParamError();
    }
    try {
        const out = s.crypto_pwhash(params.outputLen, args.passphrase, args.salt, params.t, params.m * MIB, s.crypto_pwhash_ALG_ARGON2ID13);
        return asKek(out);
    }
    finally {
        wipe(args.passphrase);
    }
}
//# sourceMappingURL=argon2id.js.map